/**
 * Object Utilities
 *
 * Helper functions for working with objects, following patterns from LogicAppsUX.
 * These provide defensive type checking to handle cases where values may not be
 * the expected type (e.g., strings instead of objects).
 */

/**
 * Returns true if a value is a plain object (not array, null, Date, RegExp, etc.).
 * Uses Object.prototype.toString for reliable type detection.
 *
 * @param value - The value to check
 * @returns True if the value is a plain object
 *
 * @example
 * isObject({}) // true
 * isObject({ a: 1 }) // true
 * isObject([]) // false
 * isObject(null) // false
 * isObject("string") // false
 * isObject(new Date()) // false
 */
export function isObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * Returns true if a value is null or undefined.
 *
 * @param value - The value to check
 * @returns True if the value is null or undefined
 */
export function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

/**
 * Gets the value for the specified property, with case-insensitive matching option.
 * Returns undefined if the object is null/undefined or the property doesn't exist.
 *
 * @param object - The object to get the property from
 * @param propertyName - The property name to look up
 * @param caseInsensitive - Whether to use case-insensitive comparison (default: true)
 * @returns The property value, or undefined if not found
 *
 * @example
 * getPropertyValue({ Foo: 'bar' }, 'foo') // 'bar' (case-insensitive)
 * getPropertyValue({ foo: 'bar' }, 'foo') // 'bar'
 * getPropertyValue(null, 'foo') // undefined
 * getPropertyValue({ foo: 'bar' }, 'baz') // undefined
 */
export function getPropertyValue(
  object: Record<string, unknown> | null | undefined,
  propertyName: string,
  caseInsensitive = true
): unknown {
  const defaultObject = object || {};

  // Security: prevent prototype pollution
  if (propertyName === '__proto__' || propertyName === 'constructor') {
    return undefined;
  }

  // Optimization: try exact match first
  const value = defaultObject[propertyName];
  if (value !== undefined) {
    return value;
  }

  // Case-insensitive fallback
  if (caseInsensitive) {
    for (const key of Object.keys(defaultObject)) {
      if (key.toLowerCase() === propertyName.toLowerCase()) {
        return defaultObject[key];
      }
    }
  }

  return undefined;
}

/**
 * Safely checks if an object has a property, handling non-object values.
 * Unlike the raw `in` operator, this won't throw on strings or primitives.
 *
 * @param object - The value to check (may not be an object)
 * @param propertyName - The property name to check for
 * @returns True if the value is an object and has the property
 *
 * @example
 * hasProperty({ foo: 'bar' }, 'foo') // true
 * hasProperty({ foo: 'bar' }, 'baz') // false
 * hasProperty('string', 'length') // false (doesn't throw)
 * hasProperty(null, 'foo') // false
 */
export function hasProperty(object: unknown, propertyName: string): boolean {
  return isObject(object) && propertyName in object;
}

/**
 * Set a value at a nested dot-notation path in an object, but only if the
 * existing leaf is truly missing (`undefined`). Intermediate objects are
 * created as needed; non-object intermediates are replaced. Returns true if
 * a value was written.
 *
 * Used to inject manifest defaults for visible-but-missing leaves so the
 * saved JSON matches what the runtime expects, without overwriting any
 * value the user has already configured — including explicit clears (`''`)
 * and explicit nulls.
 *
 * @example
 * setNestedDefault({}, 'a.b.c', 1)              // → { a: { b: { c: 1 } } }, returns true
 * setNestedDefault({ a: { b: { c: 0 } } }, 'a.b.c', 1) // unchanged, returns false
 * setNestedDefault({ a: { b: { c: '' } } }, 'a.b.c', 1) // unchanged, returns false (user cleared)
 */
export function setNestedDefault(
  target: Record<string, unknown>,
  path: string,
  value: unknown
): boolean {
  const parts = path.split('.');
  if (parts.length === 0 || parts[0] === '') return false;

  let current = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    const next = current[part];
    if (!isObject(next)) {
      const created: Record<string, unknown> = {};
      current[part] = created;
      current = created;
    } else {
      current = next;
    }
  }

  const last = parts[parts.length - 1]!;
  if (current[last] !== undefined) return false;

  current[last] = value;
  return true;
}

/**
 * Delete a value at a nested dot-notation path in an object, if present.
 * No-op if any intermediate segment is missing or not an object.
 * Returns true if a value was removed.
 *
 * @example
 * deleteNestedKey({ a: { b: 1 } }, 'a.b')  // → { a: {} }, returns true
 * deleteNestedKey({ a: {} }, 'a.b')        // unchanged, returns false
 */
export function deleteNestedKey(obj: Record<string, unknown>, path: string): boolean {
  const parts = path.split('.');
  if (parts.length === 0 || parts[0] === '') return false;

  let current: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i]!;
    const next = current[part];
    if (!isObject(next)) return false;
    current = next;
  }

  const last = parts[parts.length - 1]!;
  if (!(last in current)) return false;
  delete current[last];
  return true;
}
